/**
 * Maps `analysis_layers.key` values (from GET /auth/me `allowed_layer_keys`) to
 * (a) map layer toggles in MapLayerManager and (b) extra feature flags for UI-only tools.
 *
 * Full catalog (keys, categories, endpoints): `analysisLayersCatalog.ts`. DB seed:
 * `digisaka-webgis-fastapi/scripts/upsert_analysis_layers_catalog.py`.
 */
import type { User } from '../types/auth';
import type { LayerVisibilityState } from '../features/shared/utils/MapLayerManager';
import { isProjectDeveloper } from '../features/carbon-projects/homePath';

const MAP_KEYS = new Set<string>([
  'vhi',
  'lst',
  'heatMap',
  'timeSeries',
  'maize',
  'sugarCane',
  'rice',
  'coconut',
  'riceForecast',
  'bsri',
  'strawBurn',
  'plantingMethodDry2025',
  'plantingMethodDry2024',
  'riceGrowthStage',
  'irrigationType',
  'cadiDrought',
  'pestRisk',
  'isabelaPhilriceNDVI',
  'isabelaPhilriceOrtho',
  'caviteSilangNDVI',
  'caviteSilangOrtho',
  'treeDetection',
]);

/** One DB `analysis_layers.key` can unlock several map toggles. */
const ENTITLEMENT_TO_LAYERS: Record<string, (keyof LayerVisibilityState)[]> = {
  vhi: ['vhi'],
  lst: ['lst'],
  heatMap: ['heatMap'],
  timeSeries: ['timeSeries'],
  maize: ['maize'],
  sugarCane: ['sugarCane'],
  sugarcane: ['sugarCane'],
  rice: ['rice'],
  rice_area: ['rice'],
  coconut: ['coconut'],
  riceForecast: ['riceForecast'],
  rice_yield_forecast: ['riceForecast'],
  bsri: ['bsri'],
  strawBurn: ['strawBurn'],
  lowCarbon: ['strawBurn'],
  plantingMethodDry2025: ['plantingMethodDry2025'],
  plantingMethodDry2024: ['plantingMethodDry2024'],
  riceGrowthStage: ['riceGrowthStage'],
  irrigationType: ['irrigationType'],
  cadiDrought: ['cadiDrought'],
  pestRisk: ['pestRisk'],
  pest_risk: ['pestRisk'],
  isabelaPhilriceNDVI: ['isabelaPhilriceNDVI'],
  isabelaPhilriceOrtho: ['isabelaPhilriceOrtho'],
  caviteSilangNDVI: ['caviteSilangNDVI'],
  caviteSilangOrtho: ['caviteSilangOrtho'],
  /** Registry drone sites (`droneSites.ts`) — panel cards, not a `LayerVisibilityState` toggle. */
  droneSites: [],
  treeDetection: ['treeDetection'],
  tree_detection: ['treeDetection'],
  /** Tab groups these tools; each row is its own entitlement — do not map to treeDetection or tree UI appears for all. */
  crownSegmentation: [],
  crown_segmentation: [],
  cropfieldDelineation: [],
  cropfield_delineation: [],
  blsForecast: [],
  bls_forecast: [],
  gptTreeObjectDetect: [],
  gptTreeCrown: [],
  gptFieldBoundaries: [],
  soilMoisture: ['heatMap'],
  soil_moisture: ['heatMap'],
  /** Digisaka GPT (chat + some server tools) — not a WMS key */
  digisakaGpt: [],
  digisaka_gpt: [],
  /** Loss & damage / flood explorer — panel tab, not a WMS key */
  floodDamage: [],
  disasterLossDamage: [],
  flood: [],
  /** Carbon-only request cards: drone flights and in-field sensors. Not a map toggle. */
  fieldServices: [],
  /** Crop calendar stream — Analysis panel tab, not a map toggle */
  cropCalendar: [],
  /** Climate time series — POST /api/climate-data/ — Analysis panel tab */
  climate: [],
  /** Field health / multi-index report — POST /api/fieldreport/ — Analysis panel tab */
  fieldReport: [],
  field_report: [],
  /** Farm-only rice forecast POST — separate from large-scale `riceForecast` header */
  farmRiceYieldForecast: [],
  farm_rice_yield_forecast: [],
  /** Multi-field index time series POST — admin matrix row (catalog only until UI gates on this key) */
  multipleFieldAnalysis: [],
  multiple_field_analysis: [],
};

/**
 * Resolves which raw `allowed_layer_keys` to pass into `buildAllowedEntitlementSet` (MapView, AnalysisPanel).
 * Any non-guest, non-superuser must never fall through to “show every layer” when the field is missing,
 * the session is still loading (`user` null), or a cached client has no `allowed_layer_keys` yet
 * — those cases use `[]` (hide tools). Superuser and system guest may keep `undefined` so `buildAllowed`
 * still shows the full catalog when the API omits the array.
 */
export function getEffectiveAllowedLayerKeysForMap(
  user: User | null | undefined
): string[] | undefined {
  if (user == null) {
    return [];
  }
  if (user.is_superuser) {
    return user.allowed_layer_keys;
  }
  if (user.is_system_guest) {
    return user.allowed_layer_keys;
  }
  let keys = user.allowed_layer_keys ?? [];
  if (!isProjectDeveloper(user.role)) return keys;
  if (!keys.some((key) => key.trim().toLowerCase() === 'fieldservices')) {
    keys = [...keys, 'fieldServices'];
  }
  return keys;
}

/**
 * @param allowed from `/auth/me` `allowed_layer_keys` (ideally preprocessed with {@link getEffectiveAllowedLayerKeysForMap})
 * - `undefined` / `null` → not filtering (show all) for backward compatibility
 * - `[]` → no tools enabled in UI
 */
export function buildAllowedEntitlementSet(
  allowed: string[] | undefined | null
): Set<string> | null {
  if (allowed == null) return null;
  const out = new Set<string>();
  for (const raw of allowed) {
    const k = raw.trim();
    if (!k) continue;
    const lower = k.toLowerCase();
    const mapped = ENTITLEMENT_TO_LAYERS[k] ?? ENTITLEMENT_TO_LAYERS[lower];
    if (mapped !== undefined) {
      if (mapped.length) {
        mapped.forEach((m) => out.add(m));
        // Keep catalog keys (e.g. crownSegmentation) for per-tool UI, not only mapped map toggles.
        out.add(k);
        out.add(lower);
      } else {
        out.add(k);
        out.add(lower);
      }
    } else if (MAP_KEYS.has(k)) {
      out.add(k);
    } else {
      out.add(k);
      out.add(lower);
    }
  }
  return out;
}

export function isEntitlementAllowed(set: Set<string> | null, key: string): boolean {
  if (set == null) return true;
  return set.has(key) || set.has(key.toLowerCase());
}

/**
 * Entitlement keys that unlock the registry drone sites (`droneSites.ts`): the dedicated
 * `droneSites` key, or any existing drone key so current Drone Data users keep seeing the section.
 */
export const DRONE_SITES_ENTITLEMENT_KEYS = [
  'droneSites',
  'isabelaPhilriceNDVI',
  'isabelaPhilriceOrtho',
  'caviteSilangNDVI',
  'caviteSilangOrtho',
] as const;

export function isDroneSitesAllowed(set: Set<string> | null): boolean {
  if (set == null) return true;
  return DRONE_SITES_ENTITLEMENT_KEYS.some((key) => isEntitlementAllowed(set, key));
}

export function isMapLayerAllowed(
  set: Set<string> | null,
  key: keyof LayerVisibilityState
): boolean {
  if (set == null) return true;
  return set.has(String(key));
}

/**
 * Vertical / horizontal tab order in `LayerTogglePanel` (large-scale Analysis panel + map dock).
 * **Admin → Role default layers** uses the same sequence for map tool groups.
 */
export const LAYER_TOGGLE_PANEL_TAB_ORDER = [
  'forecast',
  'multiTemporal',
  'droneData',
  'fieldServices',
  'cropArea',
  'disasterLossDamage',
  'lowCarbon',
  'treeDetection',
] as const;

export type LayerTogglePanelTabId = (typeof LAYER_TOGGLE_PANEL_TAB_ORDER)[number];

const TAB_TO_ANY_KEYS: Record<LayerTogglePanelTabId, string[]> = {
  /** Large-scale regional forecast only — farm POST is `farmRiceYieldForecast`. */
  forecast: ['riceForecast', 'rice_yield_forecast'],
  /** Large-scale Multi-temporal maps — farm Index Map / Time Series stay under Farm analysis. */
  multiTemporal: [
    'bsri',
    'vhi',
    'lst',
    'plantingMethodDry2025',
    'plantingMethodDry2024',
    'riceGrowthStage',
    'irrigationType',
    'cadiDrought',
    'pestRisk',
    ],
  droneData: [
    'isabelaPhilriceNDVI',
    'isabelaPhilriceOrtho',
    'caviteSilangNDVI',
    'caviteSilangOrtho',
    'droneSites',
  ],
  fieldServices: ['fieldServices'],
  cropArea: ['maize', 'sugarCane', 'rice', 'coconut'],
  disasterLossDamage: ['floodDamage', 'disasterLossDamage', 'flood'],
  lowCarbon: ['strawBurn'],
  treeDetection: [
    'treeDetection',
    'tree_detection',
    'crownSegmentation',
    'crown_segmentation',
    'cropfieldDelineation',
    'cropfield_delineation',
    'gptTreeObjectDetect',
    'gptTreeCrown',
    'gptFieldBoundaries',
  ],
};

/** Keys under the shared “Tree, Crown & Field Boundaries” panel tab. */
const TREE_TAB_INFERENCE_TOOL_KEYS = {
  tree: [
    'treeDetection',
    'tree_detection',
    'gptTreeObjectDetect',
  ],
  crown: ['crownSegmentation', 'crown_segmentation', 'gptTreeCrown'],
  field: [
    'cropfieldDelineation',
    'cropfield_delineation',
    'gptFieldBoundaries',
  ],
} as const;

/**
 * Whether the user may use a specific inference tool in the tree/crown/field tab.
 * (Superuser / legacy: `set === null` → all tools shown.)
 */
export function isTreeTabInferenceToolAllowed(
  set: Set<string> | null,
  tool: keyof typeof TREE_TAB_INFERENCE_TOOL_KEYS
): boolean {
  if (set == null) return true;
  return TREE_TAB_INFERENCE_TOOL_KEYS[tool].some((alias) =>
    isEntitlementAllowed(set, alias)
  );
}

export function isPanelTabUnlocked(
  set: Set<string> | null,
  tab: LayerTogglePanelTabId
): boolean {
  if (set == null) return true;
  const keys = TAB_TO_ANY_KEYS[tab];
  if (!keys.length) return false;
  return keys.some((k) => isEntitlementAllowed(set, k));
}

/** Farm-level rice yield tab — not the large-scale forecast header. */
export function isFarmRiceYieldForecastAllowed(set: Set<string> | null): boolean {
  return (
    isEntitlementAllowed(set, 'farmRiceYieldForecast') ||
    isEntitlementAllowed(set, 'farm_rice_yield_forecast')
  );
}

/** Keys that unlock the Farm analysis top-nav / rail icon. */
const FARM_ANALYSIS_NAV_KEYS = [
  'heatMap',
  'timeSeries',
  'cropCalendar',
  'climate',
  'fieldReport',
  'field_report',
  'farmRiceYieldForecast',
  'farm_rice_yield_forecast',
  'multipleFieldAnalysis',
  'multiple_field_analysis',
  'soilMoisture',
  'soil_moisture',
  'blsForecast',
  'bls_forecast',
] as const;

/** Hide the Farm analysis header when none of its inner tools are assigned. */
export function isFarmAnalysisNavUnlocked(set: Set<string> | null): boolean {
  if (set == null) return true;
  return FARM_ANALYSIS_NAV_KEYS.some((k) => isEntitlementAllowed(set, k));
}

export function shouldShowDigisakaGpt(allowed: string[] | undefined | null): boolean {
  const s = buildAllowedEntitlementSet(allowed);
  if (s == null) return true;
  if (s.size === 0) return false;
  return isEntitlementAllowed(s, 'digisakaGpt') || isEntitlementAllowed(s, 'digisaka_gpt');
}
